const _colors = {
  // NEW COLORS
  primary: {
    anthracite: '#393A41',
    grey: '#F8F8F8',
    white: '#fff',
  },
  secondary: {
    grey: '#d8d8d8',
    green: '#335d17',
    link: '#008DB1',
  },
  brand_new: {
    redDark: '#B40B1A',
    redRemondis: '#E2001A',
  },
  common: {
    black: '#000',
  },
  other: {
    grey: '#E2DFDD',
  },
  wasteHierarchy: {
    step1: { light: '#B40B1A1a', dark: '#B40B1A' },
    step2: { light: '#ffa8001a', dark: '#ffa800' },
    step3: { light: '#275a401a', dark: '#275a40' },
    step4: { light: '#0154911a', dark: '#015491' },
    step5: { light: '#535F6A1a', dark: '#535F6A' },
  },

  // OLD COLORS
  gray: '#cbcfd2',
  grayMedium: '#EEEFF0',
  grayDark: '#666f76',
  ui: {
    green: '#275a40',
    yellow: '#ffa800',
    red: '#E2001A',
    blue: '#0d43f3',
    gray: '#f3f3f3',
    orange: '#e84e26',
    greenDark: '#6f9a18',
    redDark: '#B40B1A',
    greenAlt: '#335D17',
  },
  brand: {
    gray: '#393a42',
    grayDark: '#1c1c1c',
    blue: '#015491',
    blueDark: '#002d5a',
    sand: '#f2f0e9',
    sandDark: '#dedbd1',
  },
  materialOccurrence: {
    bg: '#f7f8fa',
    border: '#d1d5db',
  },
};

const _containerSpacing = {
  topPadding: '3rem',
  bottomPadding: '3rem',
};

const _sizes = {
  impersonateBarHeight: '3rem',
  impersonateBarHeightMobile: '3.5rem',

  serviceContainerTopPadding: _containerSpacing.topPadding,
  serviceContainerBottomPadding: _containerSpacing.bottomPadding,

  serviceSidebarGap: '8px',
  serviceBottomWrapperHeight: '81px',

  servicesWeekRowWidth: '950px',
  serviceConfirmationRowHeight: '134px',
};

const calcOffset = (value: string) => (value === '0' ? '' : `- ${value}`);

const _summarySizes = {
  width: '460px',

  topOffset: _containerSpacing.topPadding,
  bottomOffset: _containerSpacing.bottomPadding,
  impersonateTopPadding: '2rem',

  get listHeight() {
    return `calc(
      100vh 
      ${calcOffset(this.topOffset)}
      - ${_sizes.serviceSidebarGap}
      - ${_sizes.serviceBottomWrapperHeight}
      ${calcOffset(this.bottomOffset)}
    )`;
  },

  get listHeightWithImpersonate() {
    return `calc(
      100vh 
      ${calcOffset(this.topOffset)}
      - ${_sizes.serviceSidebarGap} 
      - ${_sizes.serviceBottomWrapperHeight} 
      ${calcOffset(this.bottomOffset)}
      - ${this.impersonateTopPadding} 
    )`;
  },

  get topOffsetCalc() {
    return this.topOffset === '0' ? this.width : `calc(${this.width} + ${this.topOffset})`;
  },
  get leftColumnWidth() {
    return `calc(100% - ${this.width} - 8px)`;
  },

  get panelHeight() {
    return this.topOffset === '0' ? '100%' : `calc(100% - ${this.topOffset})`;
  },
  get panelHeightWithImpersonate() {
    return `calc(100% - ${_sizes.impersonateBarHeight} - ${this.impersonateTopPadding})`;
  },
};

const theme = {
  color: {
    primary: {
      anthracite: _colors.primary.anthracite,
      grey: _colors.primary.grey,
      white: _colors.primary.white,
    },
    secondary: {
      grey: _colors.secondary.grey,
      green: _colors.secondary.green,
      link: _colors.secondary.link,
    },
    brand_new: {
      redDark: _colors.brand_new.redDark,
      redRemondis: _colors.brand_new.redRemondis,
    },
    common: {
      black: _colors.common.black,
    },
    other: {
      grey: _colors.other.grey,
    },
    bg: {
      main: _colors.primary.grey,
      secondary: _colors.primary.anthracite,
      alt: _colors.primary.white,
      medium: _colors.grayMedium,
    },
    brand: {
      primary: {
        main: _colors.brand_new.redRemondis,
        secondary: _colors.brand_new.redDark,
        hover: _colors.brand_new.redDark,
        text: _colors.primary.white,
      },
      secondary: {
        hover: _colors.brand.grayDark,
        text: _colors.primary.white,
      },
    },
    border: {
      anthracite: _colors.primary.anthracite,
      main: _colors.gray,
      alt: _colors.grayDark,
      default: _colors.secondary.grey,
    },
    text: {
      main: _colors.primary.anthracite,
      alt: _colors.grayDark,
      inverted: _colors.primary.white,
    },
    link: {
      main: _colors.brand.blue,
      hover: _colors.brand.blueDark,
    },
    disabled: {
      bg: _colors.ui.gray,
      alt: _colors.secondary.grey,
      text: _colors.grayDark,
    },
    ui: {
      success: _colors.ui.green,
      successText: _colors.primary.white,
      warning: _colors.ui.yellow,
      warningText: _colors.primary.anthracite,
      error: _colors.brand_new.redRemondis,
      errorText: _colors.primary.white,
      info: _colors.ui.blue,
      infoText: _colors.primary.white,
      live: _colors.ui.orange,
      liveText: _colors.primary.white,
      signalPositive: _colors.ui.greenDark,
      signalNegative: _colors.brand_new.redDark,
      compareColor: _colors.gray,
      successAlt: _colors.ui.greenAlt,
    },
    overlay: {
      main: 'rgba(0, 0, 0, 0.25)',
      alt: 'rgba(0, 0, 0, 0.1)',
    },
    wasteHierarchy: {
      step1: { light: _colors.wasteHierarchy.step1.light, dark: _colors.wasteHierarchy.step1.dark },
      step2: { light: _colors.wasteHierarchy.step2.light, dark: _colors.wasteHierarchy.step2.dark },
      step3: { light: _colors.wasteHierarchy.step3.light, dark: _colors.wasteHierarchy.step3.dark },
      step4: { light: _colors.wasteHierarchy.step4.light, dark: _colors.wasteHierarchy.step4.dark },
      step5: { light: _colors.wasteHierarchy.step5.light, dark: _colors.wasteHierarchy.step5.dark },
    },
    bar: {
      default: _colors.primary.grey,
    },
    materialOccurrence: {
      bg: _colors.materialOccurrence.bg,
      border: _colors.materialOccurrence.border,
    },
  },
  breakpoint: {
    LtSm: '@media (max-width: 599px)',
    Sm: '@media (min-width: 600px)',
    LtMd: '@media (max-width: 899px)',
    Md: '@media (min-width: 900px)',
    MdCustom: '@media (min-width: 769px)',
    LtLg: '@media (max-width: 1199px)',
    Lg: '@media (min-width: 1200px)',
    LgCustom: '@media (min-width: 1025px)',
    LtLgCustom: '@media (max-width: 1025px)',
  },
  font: {
    family: '"Nunito Sans Variable", sans-serif',
    lineHeight: {
      none: '1', // Extremely tight — for large display headings or logos
      tight: '1.2', // Tight spacing — good for UI elements, headings, nav
      snug: '1.4', // Balanced — ideal for compact paragraphs or card content
      normal: '1.5', // Standard — most common for body text
      relaxed: '1.6', // Comfortable — great for body text, articles, or long-form reading
    },
    weight: {
      extraLight: '200',
      light: '300',
      regular: '400',
      medium: '500',
      semiBold: '600',
      bold: '700',
      extraBold: '800',
    },
    stretch: {
      regular: '100%',
      condensed: '100%',
    },
    size: {
      xsmall: '0.75rem', // 12px
      small: '0.875rem', // 14px
      regular: '1rem', // 16px
      large: '1.125rem', // 18px
      xlarge: '1.5rem', // 24px
      xxlarge: '1.75rem', // 28px
      xxxlarge: '2.5rem', // 40px
    },
    letterSpacing: {
      none: '0',
      regular: '-0.4px',
      default: '0.4px',
      normal: '0.8px',
    },
  },
  size: {
    baseUnit: '8px',
    containerMaxWidth: '1600px',
    containerGutterPublic: '56px',
    containerGutter: '1.5rem',
    containerGutterLtSm: '1rem',
    contentMaxWidth: '680px',
    inputHeight: '3.5rem',
    logoHeight: '3rem',
    sideBarWidth: '260px',
    impersonateBarHeight: _sizes.impersonateBarHeight,
    impersonateBarHeightMobile: _sizes.impersonateBarHeightMobile,
    mobileMenuHeaderHeight: '76px',
    serviceButtonBarHeight: '92px',
    borderRadius: '10px',
    summary: {
      panel: {
        width: _summarySizes.width,
        top: _summarySizes.topOffset,
        height: _summarySizes.panelHeight,
        heightWithImpersonate: _summarySizes.panelHeightWithImpersonate,
      },

      list: {
        height: _summarySizes.listHeight,
        heightWithImpersonate: _summarySizes.listHeightWithImpersonate,
      },

      layout: {
        topOffset: _summarySizes.topOffsetCalc,
        leftColumnWidth: _summarySizes.leftColumnWidth,
      },
    },
    service: {
      containerTopPadding: _sizes.serviceContainerTopPadding,
      servicePaddingTopWithImpersonate: `calc(${_sizes.impersonateBarHeight} + ${_summarySizes.impersonateTopPadding})`,
      sidebarGap: _sizes.serviceSidebarGap,
      containerBottomPadding: _sizes.serviceContainerBottomPadding,
      bottomWrapperHeight: _sizes.serviceBottomWrapperHeight,
      weekRowWidth: _sizes.servicesWeekRowWidth,
      serviceConfirmationRowHeight: _sizes.serviceConfirmationRowHeight,
    },
  },
  zindex: {
    stateBar: '1100',
    panel: '9999999999', // This is only set this high to be visible over the imBox-widget (used to be 70)
    mobileMenu: '80',
    snackbar: '90',
    fullscreenDialog: '100',
    modal: '1030',
    // Dropdowns (filter, datum) ska ligga över den fasta sidomenyn men under modaler
    dropdown: '1025',
    sidebar: {
      container: '1020',
      nav: '1021',
      mobileHeader: '1030',
    },
  },
};

export type Theme = typeof theme;

export default theme;
